import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import {
	FUTURE_CONTRACT_LUMP_SUM_MAX,
	FUTURE_CONTRACT_MONTHLY_MAX,
	FUTURE_CONTRACT_NAME_MAX_LENGTH,
	FUTURE_CONTRACT_NOTE_MAX_LENGTH,
	FUTURE_CONTRACT_START_MAX_YEARS,
} from "web_myplann/Goals/constants";
import { isLoanFutureType } from "web_myplann/Goals/Helpers/futureContractForm";
import { getAmountMaxMessage } from "web_myplann/Goals/Helpers/getAmountMaxMessage";
import { getMaxFutureContractStartDate } from "web_myplann/Goals/Helpers/getMaxFutureContractStartDate";
import { getMinFutureContractStartDate } from "web_myplann/Goals/Helpers/getMinFutureContractStartDate";
import { mapFuturePortfolioToFormValues } from "web_myplann/Goals/Mappers/mapFutureContractForm";
import {
	FuturePortfolioTypeEnum,
	type GoalFuturePortfolioResponseDTO,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import type { TranslateFunction } from "web_myplann/Shared/TranslateFunction";
import { dateInMonthRange } from "web_myplann/Shared/Validation/dateInMonthRange";
import { z } from "zod";

const getFormSchema = (
	t: TranslateFunction,
	minStartDate: Date,
	maxStartDate: Date,
) => {
	const lumpSumMaxMessage = getAmountMaxMessage(
		t,
		FUTURE_CONTRACT_LUMP_SUM_MAX,
	);
	const monthlyMaxMessage = getAmountMaxMessage(t, FUTURE_CONTRACT_MONTHLY_MAX);

	return z.object({
		name: z
			.string()
			.trim()
			.min(1, t(Resources.Form.Validations.required))
			.max(
				FUTURE_CONTRACT_NAME_MAX_LENGTH,
				t(Resources.Goals.Form.nameMaxLength, {
					max: FUTURE_CONTRACT_NAME_MAX_LENGTH,
				}),
			),
		type: z.nativeEnum(FuturePortfolioTypeEnum),
		monthly: z
			.number()
			.min(0)
			.max(FUTURE_CONTRACT_MONTHLY_MAX, monthlyMaxMessage),
		lumpSum: z
			.number()
			.min(0)
			.max(FUTURE_CONTRACT_LUMP_SUM_MAX, lumpSumMaxMessage),
		loanAmount: z
			.number()
			.min(0)
			.max(FUTURE_CONTRACT_LUMP_SUM_MAX, lumpSumMaxMessage),
		startDate: dateInMonthRange(z.date(), minStartDate, maxStartDate, {
			tooSoonMessage: t(Resources.Goals.Form.Validations.startDateTooSoon),
			tooFarMessage: t(Resources.Goals.Form.Validations.startDateTooFar, {
				years: FUTURE_CONTRACT_START_MAX_YEARS,
			}),
		}),
		note: z.string().max(FUTURE_CONTRACT_NOTE_MAX_LENGTH).optional(),
	});
};

export type PinFutureContractFormValues = z.infer<
	ReturnType<typeof getFormSchema>
>;

export const usePinFutureContractForm = (
	open: boolean,
	editing?: GoalFuturePortfolioResponseDTO | null,
) => {
	const { t } = useResource();

	const minStartDate = useMemo(() => getMinFutureContractStartDate(), []);
	const maxStartDate = useMemo(() => getMaxFutureContractStartDate(), []);
	const resolver = useMemo(
		() => zodResolver(getFormSchema(t, minStartDate, maxStartDate)),
		[t, minStartDate, maxStartDate],
	);

	const form = useForm<PinFutureContractFormValues>({
		resolver,
		defaultValues: mapFuturePortfolioToFormValues(editing),
	});

	const { watch, formState, reset } = form;

	useEffect(() => {
		if (open) {
			reset(mapFuturePortfolioToFormValues(editing));
		}
	}, [open, editing, reset]);

	const type = watch("type");
	const monthly = watch("monthly");
	const lumpSum = watch("lumpSum");
	const loanAmount = watch("loanAmount");
	const name = watch("name");

	const isLoanType = isLoanFutureType(type);

	const canSubmit =
		name.trim().length > 0 &&
		(isLoanType ? loanAmount > 0 : monthly > 0 || lumpSum > 0) &&
		!formState.isSubmitting;

	const typeOptions = useMemo(
		() =>
			Object.values(FuturePortfolioTypeEnum).map((value) => ({
				value,
				label: t(Resources.Goals.Detail.Solution.Pin.Future.Types[value]),
			})),
		[t],
	);

	return {
		form,
		typeOptions,
		isLoanType,
		canSubmit,
		minStartDate,
		maxStartDate,
	};
};
